/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, March 20th 2022, 2:11:04 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  IDynamicMatrixSettings,
  IDynamicMatrixSettingsValidationSchema,
  IDynamicMatrixColumn,
  IDynamicMatrixColumnValidationSchema,
  IDynamicMatrixColumnChoiceValidationSchema,
  DynamicMatrixColumnTypes,
  FormField,
  IDynamicMatrixColumnChoice,
} from "lib/schemas";
import { useOSAPIFormContext } from "ui-widgets/forms/providers";
import {
  OSForm,
  TextFormInput,
  NestedOne2ManyPopupFormInput,
  RelatedObjectOSFormProps,
  DropDownFormInput,
  SwitchFormInput,
} from "ui-widgets/forms";
import { useForm } from "react-hook-form";
import { ValidationResolver } from "lib/validation";
import { Grid, Stack } from "@mui/material";
import { HelperText } from "ui-widgets/typography/CustomTypography";

export const DynamicMatrixFieldSettings = () => {
  const {
    useFormReturn: { setValue, watch },
  } = useOSAPIFormContext<FormField>();

  const newSettingsDefault: IDynamicMatrixSettings = {
    add_button_text: "",
    minimum_rows: 1,
    starting_rows: 1,
    columns: [],
  };

  const defaultSettings = watch<keyof FormField>("dynamic_matrix_settings") ?? newSettingsDefault;

  const useDynamicMatrixSettingsFormReturn = useForm<any>({
    defaultValues: defaultSettings,
    mode: "onTouched",
    resolver: ValidationResolver(IDynamicMatrixSettingsValidationSchema),
  });

  // Watch of all the fields of the dynamic matrix form and save the result.
  React.useEffect(() => {
    setValue("dynamic_matrix_settings", useDynamicMatrixSettingsFormReturn.watch(), { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, JSON.stringify(useDynamicMatrixSettingsFormReturn.watch())]);

  const newColumnDefault: IDynamicMatrixColumn = {
    label: null,
    name: null,
    type: "text",
    choices: [],
    default: null,
    required: true,
  };

  return (
    <OSForm
      onFormSubmit={() => {}}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => e.stopPropagation()}
      validationSchema={IDynamicMatrixSettingsValidationSchema}
      showSubmitButton={false}
      useFormReturn={useDynamicMatrixSettingsFormReturn}
    >
      <Grid container spacing={2}>
        <Grid item md={6}>
          <TextFormInput<IDynamicMatrixSettings>
            label="Enter default Starting Rows"
            schema_field_key="starting_rows"
            type="number"
          />
        </Grid>
        <Grid item md={6}>
          <TextFormInput<IDynamicMatrixSettings>
            label="Enter Minimum Rows"
            schema_field_key="minimum_rows"
            type="number"
          />
        </Grid>
        <Grid item md={12}>
          <TextFormInput<IDynamicMatrixSettings>
            label="Enter text to display on the add button"
            schema_field_key="add_button_text"
          />
        </Grid>
        <Grid item md={12}>
          <NestedOne2ManyPopupFormInput<IDynamicMatrixSettings, IDynamicMatrixColumn>
            renderRelatedObjectForm={({ currentEditItem, onChangeRelatedItem, handleClose }) => (
              <MatrixColumnForm
                defaultItemValue={currentEditItem ?? newColumnDefault}
                onFormCancel={handleClose}
                handleValidForm={onChangeRelatedItem}
              />
            )}
            popUpTitle="Dynamic Matrix Field Settings"
            getOptionLabel={(option) => option.label}
            schema_field_key="columns"
            label="Add Column Input"
            itemIdProperty={null}
          />
        </Grid>
      </Grid>
    </OSForm>
  );
};

const MatrixColumnForm = (props: RelatedObjectOSFormProps<IDynamicMatrixColumn>) => {
  const useDynamicMatrixColumnFormReturn = useForm<any>({
    defaultValues: props.defaultItemValue,
    mode: "onTouched",
    resolver: ValidationResolver(IDynamicMatrixColumnValidationSchema),
  });

  const { watch } = useDynamicMatrixColumnFormReturn;

  const watchedFieldType = watch<keyof IDynamicMatrixColumn>("type");

  return (
    <OSForm
      onFormSubmit={useDynamicMatrixColumnFormReturn.handleSubmit(props.handleValidForm)}
      onFormCancel={props.onFormCancel}
      validationSchema={IDynamicMatrixSettingsValidationSchema}
      showSubmitButton={true}
      useFormReturn={useDynamicMatrixColumnFormReturn}
    >
      <TextFormInput<IDynamicMatrixColumn> schema_field_key="name" label="Input Name" />
      <TextFormInput<IDynamicMatrixColumn> schema_field_key="label" label="Input Label" />
      <SwitchFormInput<IDynamicMatrixColumn> label="Required" schema_field_key="required" />
      <TextFormInput<IDynamicMatrixColumn> schema_field_key="default" label="Default Value" />
      <DropDownFormInput<IDynamicMatrixColumn>
        schema_field_key="type"
        label="Field Type"
        options={DynamicMatrixColumnTypes}
      />
      {watchedFieldType === "select" && (
        <Stack spacing={2}>
          <HelperText>Add choices for the select field</HelperText>
          <NestedOne2ManyPopupFormInput<IDynamicMatrixColumn, IDynamicMatrixColumnChoice>
            renderRelatedObjectForm={({ currentEditItem, onChangeRelatedItem, handleClose }) => (
              <MatrixColumnChoiceForm
                defaultItemValue={currentEditItem ?? { option: null, value: null }}
                onFormCancel={handleClose}
                handleValidForm={onChangeRelatedItem}
              />
            )}
            popUpTitle="Dynamic Matrix Field Choices"
            getOptionLabel={(option) => option.option}
            schema_field_key="choices"
            label="Add Choice"
            itemIdProperty={null}
          />
        </Stack>
      )}
    </OSForm>
  );
};

const MatrixColumnChoiceForm = (props: RelatedObjectOSFormProps<IDynamicMatrixColumnChoice>) => {
  const useDynamicMatrixColumnChoiceFormReturn = useForm<any>({
    defaultValues: props.defaultItemValue,
    mode: "onTouched",
    resolver: ValidationResolver(IDynamicMatrixColumnChoiceValidationSchema),
  });

  return (
    <OSForm
      onFormSubmit={useDynamicMatrixColumnChoiceFormReturn.handleSubmit(props.handleValidForm)}
      onFormCancel={props.onFormCancel}
      validationSchema={IDynamicMatrixSettingsValidationSchema}
      showSubmitButton={true}
      useFormReturn={useDynamicMatrixColumnChoiceFormReturn}
    >
      <TextFormInput<IDynamicMatrixColumnChoice> schema_field_key="value" label="Value" />
      <TextFormInput<IDynamicMatrixColumnChoice> schema_field_key="option" label="Option/Label" />
    </OSForm>
  );
};
